import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  CheckCircle, XCircle, Clock, Loader2, Download, FileText, ArrowRight, RotateCcw,
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POLL_DELAYS_MS = [800, 1500, 2500, 4000, 6000, 8000, 12000, 15000]; // backoff up to ~50s

const STATUS_META = {
  pending_payment: {
    icon: Clock,
    title: "Paiement en cours de validation",
    desc: "Nous attendons la confirmation de Mollie. Cela ne prend généralement que quelques secondes.",
    color: '#5493CC',
    bg: '#F2F8FD',
  },
  confirmed: {
    icon: CheckCircle,
    title: "Paiement confirmé — commande validée",
    desc: "Merci ! Votre paiement a été accepté. Votre linge est désormais prêt à être préparé.",
    color: '#16A34A',
    bg: '#F0FDF4',
  },
  payment_failed: {
    icon: XCircle,
    title: "Paiement refusé",
    desc: "Votre banque a refusé la transaction. Aucune somme n'a été débitée. Vous pouvez réessayer avec un autre moyen de paiement.",
    color: '#DC2626',
    bg: '#FEF2F2',
  },
  payment_expired: {
    icon: XCircle,
    title: "Session de paiement expirée",
    desc: "La page de paiement Mollie est restée ouverte trop longtemps. Aucune somme n'a été débitée.",
    color: '#DC2626',
    bg: '#FEF2F2',
  },
  payment_canceled: {
    icon: XCircle,
    title: "Paiement annulé",
    desc: "Vous avez annulé le paiement. Aucune somme n'a été débitée.",
    color: '#DC2626',
    bg: '#FEF2F2',
  },
};


const CheckoutReturnPage = () => {
  const { token, loading: authLoading } = useAuth();
  const { clearCart } = useCart();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('orderId');

  const [order, setOrder] = useState(null);
  const [polling, setPolling] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const cartCleared = useRef(false);
  const pollIndex = useRef(0);

  const fetchStatus = useCallback(async () => {
    if (!token || !orderId) return null;
    try {
      // Trigger Mollie sync (in case webhook hasn't arrived yet)
      const sync = await axios.post(
        `${API}/payments/mollie/sync/${orderId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const detail = await axios.get(`${API}/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const merged = { ...detail.data, ...sync.data };
      setOrder(merged);
      return merged;
    } catch (err) {
      return null;
    }
  }, [token, orderId]);

  // Poll until status is final (confirmed / failed / expired / canceled)
  useEffect(() => {
    if (authLoading || !token || !orderId) return;
    let active = true;

    const tick = async () => {
      const data = await fetchStatus();
      if (!active) return;

      if (data && data.status === 'confirmed' && !cartCleared.current) {
        cartCleared.current = true;
        clearCart();
        localStorage.removeItem('pending_order_id');
      }

      const isFinal = data && [
        'confirmed', 'payment_failed', 'payment_expired', 'payment_canceled',
      ].includes(data.status);

      if (isFinal || pollIndex.current >= POLL_DELAYS_MS.length - 1) {
        setPolling(false);
        return;
      }

      const delay = POLL_DELAYS_MS[pollIndex.current];
      pollIndex.current += 1;
      setTimeout(tick, delay);
    };

    tick();
    return () => { active = false; };
  }, [authLoading, token, orderId, fetchStatus, clearCart]);

  const handleDownload = async () => {
    if (!order) return;
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/orders/${order.id || orderId}/invoice`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `facture-terreetmer-${order.invoice_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast({
        title: 'Erreur',
        description: "Impossible de télécharger la facture pour le moment.",
        variant: 'destructive',
      });
    } finally {
      setDownloading(false);
    }
  };

  const handleRetryPayment = async () => {
    if (!order) return;
    // Re-create a fresh payment by re-submitting checkout
    navigate('/checkout');
  };

  if (authLoading || (!order && polling)) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center px-6 py-12">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-[#E8945A] mx-auto mb-4" />
          <p className="text-[#475569] text-sm">
            Vérification du statut du paiement…
          </p>
        </div>
      </div>
    );
  }

  // Polling finished but order info is still missing → give a clear escape hatch
  if (!order) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center px-6 py-12">
        <div className="max-w-lg w-full text-center bg-white border border-gray-200 rounded-2xl p-8">
          <div className="w-16 h-16 rounded-full bg-[#FFF5ED] flex items-center justify-center mx-auto mb-5">
            <Clock className="h-8 w-8 text-[#E8945A]" />
          </div>
          <h1 className="font-['Playfair_Display'] text-2xl font-medium text-[#2D3E50] mb-3">
            Confirmation en cours
          </h1>
          <p className="text-[#475569] text-sm mb-6">
            Votre paiement a bien été initié, mais nous n'avons pas encore reçu
            la confirmation finale. Si votre carte a été débitée, vous recevrez
            un email dans les prochaines minutes et la commande apparaîtra dans
            votre espace.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={() => navigate('/dashboard')}
              className="bg-[#2D3E50] hover:bg-[#3D4F61] gap-2 h-11"
              data-testid="return-fallback-dashboard"
            >
              Voir mes commandes
              <ArrowRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              onClick={() => window.location.reload()}
              className="gap-2 h-11"
              data-testid="return-fallback-refresh"
            >
              <RotateCcw className="h-4 w-4" />
              Rafraîchir
            </Button>
          </div>
          <p className="text-xs text-[#64748B] mt-6">
            En cas de problème :
            {' '}
            <a href="mailto:contact@terreetmer-locationdelinge.fr" className="text-[#E8945A] hover:underline">
              contact@terreetmer-locationdelinge.fr
            </a>
          </p>
        </div>
      </div>
    );
  }

  const meta = STATUS_META[order.status] || STATUS_META.pending_payment;
  const Icon = meta.icon;
  const isConfirmed = order.status === 'confirmed';
  const isFailed = ['payment_failed', 'payment_expired', 'payment_canceled'].includes(order.status);

  return (
    <div className="min-h-screen pt-20 md:pt-24 pb-12 px-6 py-12">
      <div className="max-w-2xl mx-auto">
        <div
          className="rounded-2xl p-8 md:p-12 text-center border-2"
          style={{ backgroundColor: meta.bg, borderColor: `${meta.color}40` }}
          data-testid={`checkout-return-${order.status}`}
        >
          <div
            className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
            style={{ backgroundColor: `${meta.color}20` }}
          >
            {polling && order.status === 'pending_payment' ? (
              <Loader2 className="h-10 w-10 animate-spin" style={{ color: meta.color }} />
            ) : (
              <Icon className="h-10 w-10" style={{ color: meta.color }} />
            )}
          </div>

          <h1
            className="font-['Playfair_Display'] text-3xl md:text-4xl font-medium mb-3"
            style={{ color: meta.color }}
          >
            {meta.title}
          </h1>
          <p className="text-[#475569] max-w-md mx-auto">{meta.desc}</p>

          {/* Order summary */}
          {isConfirmed && order.invoice_number && (
            <div className="mt-8 inline-block text-left bg-white rounded-xl border border-gray-100 px-6 py-5 shadow-sm">
              <div className="flex items-center gap-3 mb-3">
                <FileText className="h-5 w-5 text-[#E8945A]" />
                <span className="text-sm font-medium text-[#2D3E50]">
                  Facture <strong>N° {order.invoice_number}</strong>
                </span>
              </div>
              <div className="text-sm text-[#64748B] space-y-0.5">
                <div>Total payé : <strong className="text-[#2D3E50]">{order.total_price?.toFixed(2)} €</strong></div>
                <div>Mode : Carte bancaire (Mollie)</div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            {isConfirmed && (
              <>
                <Button
                  onClick={handleDownload}
                  disabled={downloading}
                  className="bg-[#E8945A] hover:bg-[#D4844A] gap-2 h-12 px-6"
                  data-testid="return-download-invoice"
                >
                  {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  Télécharger ma facture (PDF)
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  className="gap-2 h-12 px-6"
                  data-testid="return-go-dashboard"
                >
                  Mon espace
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </>
            )}

            {isFailed && (
              <>
                <Button
                  onClick={handleRetryPayment}
                  className="bg-[#E8945A] hover:bg-[#D4844A] gap-2 h-12 px-6"
                  data-testid="return-retry"
                >
                  <RotateCcw className="h-4 w-4" />
                  Recommencer la commande
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/')}
                  className="h-12 px-6"
                >
                  Retour à l'accueil
                </Button>
              </>
            )}

            {!isConfirmed && !isFailed && polling && (
              <p className="text-xs text-[#64748B]">
                Cette page se met à jour automatiquement.
              </p>
            )}
          </div>

          {isConfirmed && (
            <p className="text-xs text-[#64748B] mt-6">
              Un email de confirmation vous sera envoyé prochainement. Vous pouvez fermer cette page.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckoutReturnPage;
